import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as usersApi from '../api/usersApi';
import { Card, EmptyState, ErrorBanner, Pagination, TableSkeleton } from '../components/ui';
import { IconSearch } from '../components/icons';
import { AppUser } from '../types/user';
import { capitalize, formatDate } from '../utils/format';

/** Signup-date presets. 'custom' reveals the two date inputs. */
const JOINED_RANGES = [
  { value: '', label: 'Any time' },
  { value: '30d', label: 'Last month (30 days)' },
  { value: '90d', label: 'Last quarter (90 days)' },
  { value: '365d', label: 'Last year (365 days)' },
  { value: 'custom', label: 'Custom range…' },
] as const;

// Local calendar date as YYYY-MM-DD. Uses the admin's own clock rather than UTC
// so "last 30 days" lines up with the date they see; signup timestamps are
// stored in UTC, so accounts created within a few hours of midnight can land on
// the neighbouring day. Immaterial for a browsing filter.
function toISODate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
}

/** Turn the preset (or custom inputs) into the concrete bounds the API takes. */
function resolveJoinedRange(
  range: string,
  from: string,
  to: string
): { joined_from?: string; joined_to?: string } {
  if (range === 'custom') {
    return { joined_from: from || undefined, joined_to: to || undefined };
  }
  const days = { '30d': 30, '90d': 90, '365d': 365 }[range];
  return days ? { joined_from: daysAgo(days), joined_to: toISODate(new Date()) } : {};
}

export default function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const purchased = searchParams.get('purchased') ?? '';
  const range = searchParams.get('range') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';

  const { joined_from, joined_to } = useMemo(
    () => resolveJoinedRange(range, from, to),
    [range, from, to]
  );
  const hasFilters = Boolean(search || purchased || range);

  const [users, setUsers] = useState<AppUser[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await usersApi.getUsers({
        search: search || undefined,
        purchased: purchased === 'yes' || purchased === 'no' ? purchased : undefined,
        joined_from,
        joined_to,
        page,
        limit: 20,
      });
      setUsers(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [search, purchased, joined_from, joined_to, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Merge a patch into the query string. Any filter change resets to page 1 —
  // staying on page 5 of a narrower result set would show an empty table.
  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    next.delete('page');
    setSearchParams(next);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setParam({ q: searchInput.trim() });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-sub">
          {total > 0
            ? `${total} ${hasFilters ? 'matching' : 'registered'} user${total === 1 ? '' : 's'}`
            : 'Customer accounts'}
        </p>
      </div>

      <form onSubmit={handleSearch} className="card flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[16rem] flex-1">
          <label className="label">Search by email or name</label>
          <div className="relative">
            <input
              className="input pr-10"
              placeholder="name or email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sub transition-colors hover:text-ink"
              aria-label="Search"
            >
              <IconSearch className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="w-full sm:w-44">
          <label className="label">Purchases</label>
          <select
            className="input"
            value={purchased}
            onChange={(e) => setParam({ purchased: e.target.value })}
          >
            <option value="">All users</option>
            <option value="yes">Has purchased</option>
            <option value="no">Never purchased</option>
          </select>
        </div>

        <div className="w-full sm:w-52">
          <label className="label">Joined</label>
          <select
            className="input"
            value={range}
            // Leaving the custom range drops any dates already entered, so a
            // stale from/to can't linger in the URL behind a preset.
            onChange={(e) =>
              setParam(
                e.target.value === 'custom'
                  ? { range: 'custom' }
                  : { range: e.target.value, from: '', to: '' }
              )
            }
          >
            {JOINED_RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {range === 'custom' && (
          <>
            <div className="w-full sm:w-40">
              <label className="label">From</label>
              <input
                className="input"
                type="date"
                max={to || undefined}
                value={from}
                onChange={(e) => setParam({ from: e.target.value })}
              />
            </div>
            <div className="w-full sm:w-40">
              <label className="label">To</label>
              <input
                className="input"
                type="date"
                min={from || undefined}
                value={to}
                onChange={(e) => setParam({ to: e.target.value })}
              />
            </div>
          </>
        )}

        {hasFilters && (
          <button type="button" className="btn-ghost" onClick={clearFilters}>
            Clear
          </button>
        )}
      </form>

      {error && <ErrorBanner message={error} onRetry={load} />}

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={8} />
        ) : users.length === 0 ? (
          <EmptyState
            message="No users found"
            hint={hasFilters ? 'Try widening or clearing the filters' : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Name</th>
                    <th className="th">Email</th>
                    <th className="th">Joined</th>
                    <th className="th">Role</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {users.map((u) => (
                    <tr key={u.id} className="transition-colors hover:bg-edge/20">
                      <td className="td">
                        <Link to={`/users/${u.id}`} className="font-medium text-gold hover:underline">
                          {u.name || '—'}
                        </Link>
                      </td>
                      <td className="td text-sub">{u.email}</td>
                      <td className="td text-sub">{formatDate(u.created_at)}</td>
                      <td className="td text-sub">{capitalize(u.role || 'user')}</td>
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <Link to={`/users/${u.id}`} className="btn-outline !px-3 !py-1.5 !text-xs">
                            View
                          </Link>
                          <Link to={`/orders?user=${u.id}`} className="btn-outline !px-3 !py-1.5 !text-xs">
                            View orders
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
